import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import apiService from '../services/apiService';
import pendingPhotosService from '../services/pendingPhotosService';
import ObservationModal from './modals/ObservationModal';
import './Etape11.css';

export default function Etape11({ mission, onComplete, onBack }) {
  const [observations, setObservations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomaine, setSelectedDomaine] = useState('all');
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [editingObservation, setEditingObservation] = useState(null);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [obsContextFromPending, setObsContextFromPending] = useState(null);
  const [obsInitialPhotosFromPending, setObsInitialPhotosFromPending] = useState([]);
  const [obsPendingPhotoId, setObsPendingPhotoId] = useState(null);
  
  const [anomalies, setAnomalies] = useState('');
  const [miseArretConseillee, setMiseArretConseillee] = useState(false);

  const isLevage = mission?.type === 'levage';

  useEffect(() => {
    if (mission?.id) {
      loadData();
    }
  }, []);

  useEffect(() => {
    if (mission?.id) {
      loadData();
    }
  }, [mission?.id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await inspectionStorage.getStep(mission.id, 'etape_11_observations');
      
      console.log('📥 Données Etape11 chargées:', data);
      
      if (data) {
        setObservations(data.observations || []);
        
        if (isLevage && data.levage) {
          setAnomalies(data.levage.anomalies || '');
          setMiseArretConseillee(data.levage.mise_arret_conseillee || false);
        }
      } else {
        setObservations([]);
      }

      await refreshPendingPhotos();
    } catch (error) {
      console.error('❌ Erreur chargement données Etape11:', error);
      setObservations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPendingPhotos = async () => {
    try {
      const list = await pendingPhotosService.list(mission.id);
      setPendingPhotos(list);
    } catch (e) {
      console.warn('[Etape11] chargement pending photos échoué :', e?.message || e);
      setPendingPhotos([]);
    }
  };

  // Retente l'upload des photos en attente dès qu'on est en ligne (et au montage si déjà en ligne)
  useEffect(() => {
    if (!mission?.id) return undefined;
    const handler = async () => {
      try {
        await pendingPhotosService.retryFailedUploads(mission.id);
        await refreshPendingPhotos();
      } catch (e) {
        console.warn('[Etape11] retry uploads échoué :', e?.message || e);
      }
    };
    window.addEventListener('online', handler);
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      handler();
    }
    return () => window.removeEventListener('online', handler);
  }, [mission?.id]);

  const saveData = async (updatedObservations, extraData = {}) => {
    try {
      const dernierNumero = updatedObservations.length > 0
        ? Math.max(...updatedObservations.map(o => o.numero || 0))
        : 0;

      const stepData = {
        observations: updatedObservations,
        dernier_numero: dernierNumero,
        ...(isLevage && {
          levage: {
            anomalies: extraData.anomalies !== undefined ? extraData.anomalies : anomalies,
            mise_arret_conseillee: extraData.mise_arret_conseillee !== undefined ? extraData.mise_arret_conseillee : miseArretConseillee
          }
        }),
        updated_at: new Date().toISOString()
      };

      // 1. Sauvegarder en local (IndexedDB)
      await inspectionStorage.saveStep(mission.id, 'etape_11_observations', stepData);
      
      // 2. 🔥 AUTO-SYNC vers Base44 (si en ligne)
      syncManager.syncStep(mission.id, 'etape_11_observations', stepData);
      
      setObservations(updatedObservations);
      console.log('✅ Données Etape11 sauvegardées:', stepData);
    } catch (error) {
      console.error('❌ Erreur sauvegarde Etape11:', error);
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  const isHauteTension = (obs) => {
    const ref = obs.reference_reglementaire || '';
    return ref.includes('NF C 13') || ref.includes('NF C13') || ref.includes('NFC13') || ref.includes('HC') || obs.domaine === 'HT';
  };

  const isBasseTension = (obs) => {
    const ref = obs.reference_reglementaire || '';
    return ref.includes('NF C 15') || ref.includes('NF C15') || ref.includes('NFC15') || ref.includes('BC') || obs.domaine === 'BT';
  };

  const filteredObservations = observations.filter(obs => {
    const matchSearch = !searchQuery ||
      obs.localisation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obs.element?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obs.constat?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obs.numero?.toString().includes(searchQuery);

    if (isLevage) return matchSearch;

    if (selectedDomaine === 'all') return matchSearch;
    if (selectedDomaine === 'HT') return matchSearch && isHauteTension(obs);
    if (selectedDomaine === 'BT') return matchSearch && isBasseTension(obs);
    return matchSearch;
  });

  const statsHT = isLevage ? 0 : observations.filter(isHauteTension).length;
  const statsBT = isLevage ? 0 : observations.filter(isBasseTension).length;

  const handleSaveObservation = async (observationData) => {
    let updatedObservations;

    const isEditing = observationData.numero !== undefined && 
                      observationData.numero !== null && 
                      observationData.numero > 0;

    if (isEditing) {
      updatedObservations = observations.map(obs =>
        obs.numero === observationData.numero ? { ...obs, ...observationData } : obs
      );
      console.log('✏️ Observation modifiée:', observationData.numero);
    } else {
      const dernierNumero = observations.length > 0
        ? Math.max(...observations.map(o => o.numero || 0))
        : 0;
      const nouveauNumero = dernierNumero + 1;
      
      const newObs = {
        ...observationData,
        numero: nouveauNumero,
        created_date: new Date().toISOString(),
        photos: observationData.photos || []
      };
      
      updatedObservations = [...observations, newObs];
      console.log('➕ Nouvelle observation créée:', nouveauNumero);
    }

    await saveData(updatedObservations);

    // Si l'observation vient d'une "photo en attente", on supprime celle-ci
    if (obsPendingPhotoId) {
      try {
        await pendingPhotosService.remove(mission.id, obsPendingPhotoId);
      } catch (e) {
        console.warn('[Etape11] suppression pending photo échouée :', e?.message || e);
      }
      await refreshPendingPhotos();
    }

    setShowObservationModal(false);
    setEditingObservation(null);
    setObsContextFromPending(null);
    setObsInitialPhotosFromPending([]);
    setObsPendingPhotoId(null);
  };

  const handleAttachPendingPhoto = (photo) => {
    setEditingObservation(null);
    setObsContextFromPending(photo.context || null);
    setObsInitialPhotosFromPending([photo.url || photo.blob_base64].filter(Boolean));
    setObsPendingPhotoId(photo.id);
    setShowObservationModal(true);
  };

  const handleDeletePendingPhoto = async (photo) => {
    if (!window.confirm('Supprimer cette photo en attente ?')) return;
    try {
      await pendingPhotosService.remove(mission.id, photo.id);
      await refreshPendingPhotos();
    } catch (e) {
      console.error('[Etape11] suppression pending photo échouée :', e);
      alert('❌ Erreur lors de la suppression.');
    }
  };

  const handleEditObservation = (obs) => {
    setEditingObservation(obs);
    setShowObservationModal(true);
  };

  const handleDeleteObservation = async (obs) => {
    if (!window.confirm(`Supprimer l'observation N°${obs.numero} ?\n\n⚠️ Les observations suivantes seront renumérotées.`)) {
      return;
    }

    const updatedObservations = observations
      .filter(o => o.numero !== obs.numero)
      .map((o, index) => ({
        ...o,
        numero: index + 1
      }));

    await saveData(updatedObservations);
    alert('✅ Observation supprimée et numérotation mise à jour !');
  };

  const fileToDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('FileReader error'));
      reader.readAsDataURL(file);
    });

  const handlePhotoUpload = async (e, obsNumero) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ La photo est trop lourde (max 5 Mo)');
      return;
    }

    setUploadingPhotoFor(obsNumero);

    try {
      let photoEntry = null;
      try {
        const result = await apiService.uploadFile(file, 'photo_observation');
        if (result?.success && result?.file_url) {
          photoEntry = result.file_url;
        }
      } catch (err) {
        console.warn('[Etape11] uploadFile:', err?.message || err);
      }

      if (!photoEntry) {
        const dataUrl = await fileToDataURL(file);
        if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
          photoEntry = dataUrl;
        } else {
          alert('❌ Erreur lors de la lecture de la photo');
          return;
        }
      }

      const updatedObservations = observations.map((obs) => {
        if (obs.numero === obsNumero) {
          return {
            ...obs,
            photos: [...(obs.photos || []), photoEntry],
          };
        }
        return obs;
      });

      await saveData(updatedObservations);
      alert('✅ Photo ajoutée !');
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert('❌ Erreur lors de l\'upload de la photo');
    } finally {
      setUploadingPhotoFor(null);
      e.target.value = '';
    }
  };

  const handleRemovePhoto = async (obsNumero, photoIndex) => {
    if (!window.confirm('Supprimer cette photo ?')) return;

    const updatedObservations = observations.map(obs => {
      if (obs.numero === obsNumero) {
        const newPhotos = [...obs.photos];
        newPhotos.splice(photoIndex, 1);
        return { ...obs, photos: newPhotos };
      }
      return obs;
    });

    await saveData(updatedObservations);
    alert('✅ Photo supprimée !');
  };

  const handleUpdateAnomalies = async (value) => {
    setAnomalies(value);
    await saveData(observations, { anomalies: value });
  };

  const handleUpdateMiseArret = async (checked) => {
    setMiseArretConseillee(checked);
    await saveData(observations, { mise_arret_conseillee: checked });
  };

  const formatEquipmentName = (element) => {
    if (!element) return 'Non spécifié';
    
    const equipmentNames = {
      'materiel_amovible': 'Matériel amovible',
      'materiel_informatique': 'Matériel informatique',
      'materiel_portatif': 'Matériel portatif',
      'materiel_industriel': 'Matériel industriel',
      'prise_courant': 'Prise de courant',
      'luminaire': 'Luminaire',
      'eclairage_securite': 'Éclairage de sécurité',
      'recepteurs_fixes': 'Récepteur fixe',
      'machine_garage': 'Machine de garage',
      'machine_ce': 'Machine CE'
    };
    
    return equipmentNames[element] || element;
  };

  const mentionConclusion = observations.length === 0
    ? "Les vérifications réalisées dans les limites de la présente mission n'ont pas fait apparaître d'anomalie ni de défectuosité."
    : "Notre vérification a fait apparaître des défectuosités auxquelles il y aura lieu de remédier.";

  if (isLoading) {
    return (
      <div className="etape11-container">
        <div className="etape11-loading">
          <div className="etape11-spinner"></div>
          <p>Chargement des observations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="etape11-container">
      {isLevage && (
        <div className="etape11-anomalies-card">
          <div className="etape11-anomalies-header">
            <h2 className="etape11-anomalies-title">⚠️ Anomalies constatées</h2>
          </div>
          <div className="etape11-anomalies-content">
            <textarea
              value={anomalies}
              onChange={(e) => handleUpdateAnomalies(e.target.value)}
              className="etape11-anomalies-textarea"
              rows={6}
              placeholder="Décrivez les anomalies constatées lors de la vérification..."
            />
            <p className="etape11-anomalies-help">
              Ces anomalies seront reportées sur le rapport final
            </p>
          </div>
        </div>
      )}

      {isLevage && (
        <div className="etape11-conclusions-card">
          <div className="etape11-conclusions-header">
            <h2 className="etape11-conclusions-title">✅ Conclusions de la vérification</h2>
          </div>
          <div className="etape11-conclusions-content">
            <div className={`etape11-mention-box ${observations.length === 0 ? 'success' : 'warning'}`}>
              <p className="etape11-mention-text">{mentionConclusion}</p>
              <p className="etape11-mention-count">
                {observations.length === 0
                  ? '✅ Aucune observation détectée'
                  : `⚠️ ${observations.length} observation(s) détectée(s)`}
              </p>
            </div>

            {observations.length > 0 && (
              <div className="etape11-mise-arret-box">
                <label className="etape11-mise-arret-label">
                  <input
                    type="checkbox"
                    checked={miseArretConseillee}
                    onChange={(e) => handleUpdateMiseArret(e.target.checked)}
                    className="etape11-mise-arret-checkbox"
                  />
                  <span>Mise à l'arrêt de l'appareil conseillée</span>
                </label>
                {miseArretConseillee && (
                  <p className="etape11-mise-arret-warning">
                    ⚠️ Cette mention sera affichée de manière visible sur le rapport final
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="etape11-header">
        <div className="etape11-header-content">
          <div>
            <h1 className="etape11-title">
              ⚠️ Étape {isLevage ? '6' : '11'} - Observations
            </h1>
            <p className="etape11-subtitle">
              Liste récapitulative de toutes les observations relevées
            </p>
          </div>
          <div className="etape11-header-badge">
            <span className="etape11-count-number">{observations.length}</span>
            <span className="etape11-count-label">observation(s)</span>
          </div>
          {pendingPhotos.length > 0 && (
            <div className="etape11-pending-badge" title="Photos en attente d'observation">
              <span className="etape11-pending-badge-icon">📷</span>
              <span className="etape11-pending-badge-value">{pendingPhotos.length}</span>
              <span className="etape11-pending-badge-label">en attente</span>
            </div>
          )}
        </div>
      </div>

      {!isLevage && (
        <div className="etape11-stats">
          <div className="etape11-stat-card ht">
            <div className="etape11-stat-content">
              <span className="etape11-stat-label">HAUTE TENSION</span>
              <span className="etape11-stat-value">{statsHT}</span>
            </div>
            <div className="etape11-stat-icon">⚡</div>
          </div>

          <div className="etape11-stat-card bt">
            <div className="etape11-stat-content">
              <span className="etape11-stat-label">BASSE TENSION</span>
              <span className="etape11-stat-value">{statsBT}</span>
            </div>
            <div className="etape11-stat-icon">📋</div>
          </div>
        </div>
      )}

      <div className="etape11-toolbar">
        <div className="etape11-search-wrapper">
          <span className="etape11-search-icon">🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par localisation, élément, constat, n°..."
            className="etape11-search-input"
          />
        </div>

        {!isLevage && (
          <div className="etape11-filters">
            <button
              className={`etape11-filter-btn ${selectedDomaine === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedDomaine('all')}
            >
              Toutes ({observations.length})
            </button>
            <button
              className={`etape11-filter-btn ${selectedDomaine === 'HT' ? 'active' : ''}`}
              onClick={() => setSelectedDomaine('HT')}
            >
              HT ({statsHT})
            </button>
            <button
              className={`etape11-filter-btn ${selectedDomaine === 'BT' ? 'active' : ''}`}
              onClick={() => setSelectedDomaine('BT')}
            >
              BT ({statsBT})
            </button>
          </div>
        )}

        <button
          onClick={() => {
            setEditingObservation(null);
            setObsContextFromPending(null);
            setObsInitialPhotosFromPending([]);
            setObsPendingPhotoId(null);
            setShowObservationModal(true);
          }}
          className="etape11-add-btn"
        >
          + Ajouter observation
        </button>
      </div>

      {pendingPhotos.length > 0 && (
        <div className="etape11-pending-section">
          <div className="etape11-pending-header">
            <h2 className="etape11-pending-title">
              📷 Photos en attente d'observation
              <span className="etape11-pending-count">({pendingPhotos.length})</span>
            </h2>
            <p className="etape11-pending-subtitle">
              Photos prises sur le terrain. Rattachez-les à une observation
              ou supprimez-les.
            </p>
          </div>

          <div className="etape11-pending-grid">
            {pendingPhotos.map((photo) => {
              const ctx = photo.context || {};
              const subtitle =
                ctx.element ||
                [ctx.armoire_designation, ctx.circuit_designation].filter(Boolean).join(' • ') ||
                'Sans contexte';
              const statusBadge = photo.upload_status === 'uploaded'
                ? { label: '☁️ Envoyée', cls: 'ok' }
                : photo.upload_status === 'failed'
                  ? { label: '⚠️ En local', cls: 'warn' }
                  : { label: '⏳ Envoi…', cls: 'pending' };
              return (
                <div key={photo.id} className="etape11-pending-card">
                  <div className="etape11-pending-thumb-wrap">
                    <img
                      src={photo.url || photo.blob_base64}
                      alt="Pending"
                      className="etape11-pending-thumb"
                    />
                    <span className={`etape11-pending-status ${statusBadge.cls}`}>
                      {statusBadge.label}
                    </span>
                  </div>
                  <div className="etape11-pending-info">
                    <p className="etape11-pending-loc">📍 {ctx.localisation || '-'}</p>
                    <p className="etape11-pending-element">{subtitle}</p>
                    <p className="etape11-pending-date">
                      {new Date(photo.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <div className="etape11-pending-actions">
                    <button
                      type="button"
                      className="etape11-pending-btn-primary"
                      onClick={() => handleAttachPendingPhoto(photo)}
                    >
                      ➕ Créer l'observation
                    </button>
                    <button
                      type="button"
                      className="etape11-pending-btn-danger"
                      onClick={() => handleDeletePendingPhoto(photo)}
                      title="Supprimer la photo"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filteredObservations.length === 0 ? (
        <div className="etape11-empty">
          <div className="etape11-empty-icon">⚠️</div>
          <p className="etape11-empty-text">
            {observations.length === 0
              ? "Aucune observation n'a été créée pour le moment."
              : 'Aucune observation ne correspond à votre recherche.'}
          </p>
        </div>
      ) : (
        <div className="etape11-observations-list">
          {filteredObservations.map((obs) => {
            const isDomaineBT = !isLevage && isBasseTension(obs);
            
            return (
              <div key={obs.numero} className={`etape11-obs-card ${isDomaineBT ? 'bt' : 'ht'}`}>
                <div className="etape11-obs-header">
                  <div className="etape11-obs-header-left">
                    <div className={`etape11-obs-numero ${isDomaineBT ? 'bt' : 'ht'}`}>
                      {obs.numero}
                    </div>
                    <div>
                      <div className="etape11-obs-badges">
                        {!isLevage && (
                          <span className={`etape11-badge ${isDomaineBT ? 'bt' : 'ht'}`}>
                            {isDomaineBT ? 'BASSE TENSION' : 'HAUTE TENSION'}
                          </span>
                        )}
                      </div>
                      <div className="etape11-obs-meta">
                        <span>📍 {obs.localisation}</span>
                        <span>🔧 {formatEquipmentName(obs.element)}</span>
                        {obs.circuit && <span>⚡ Circuit: {obs.circuit}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="etape11-obs-actions">
                    <button
                      onClick={() => handleEditObservation(obs)}
                      className="etape11-action-btn edit"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteObservation(obs)}
                      className="etape11-action-btn delete"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="etape11-obs-content">
                  <div className="etape11-obs-section">
                    <p className="etape11-obs-label">Constat / Préconisation</p>
                    <p className="etape11-obs-text">{obs.constat}</p>
                  </div>

                  <div className="etape11-obs-section">
                    <p className="etape11-obs-label">Référence réglementaire</p>
                    <p className="etape11-obs-reference">{obs.reference_reglementaire || '-'}</p>
                  </div>

                  <div className="etape11-obs-section">
                    <div className="etape11-photos-header">
                      <p className="etape11-obs-label">Photos ({obs.photos?.length || 0})</p>
                      <label className="etape11-photo-upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhotoUpload(e, obs.numero)}
                          className="etape11-photo-input"
                          disabled={uploadingPhotoFor === obs.numero}
                        />
                        <button
                          type="button"
                          disabled={uploadingPhotoFor === obs.numero}
                          className="etape11-photo-upload-btn"
                          onClick={(e) => e.currentTarget.previousElementSibling.click()}
                        >
                          {uploadingPhotoFor === obs.numero ? '⏳ Upload...' : '📷 Ajouter photo'}
                        </button>
                      </label>
                    </div>

                    {obs.photos && obs.photos.length > 0 ? (
                      <div className="etape11-photos-grid">
                        {obs.photos.map((photo, photoIndex) => (
                          <div key={photoIndex} className="etape11-photo-item">
                            <img src={photo} alt={`Photo ${photoIndex + 1}`} />
                            <button
                              onClick={() => handleRemovePhoto(obs.numero, photoIndex)}
                              className="etape11-photo-delete"
                              title="Supprimer"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="etape11-photos-empty">Aucune photo ajoutée</p>
                    )}
                  </div>

                  <div className="etape11-obs-footer">
                    <span className="etape11-obs-date">
                      Créée le {new Date(obs.created_date).toLocaleDateString('fr-FR')}
                    </span>
                    {obs.custom && (
                      <span className="etape11-obs-custom-badge">✏️ Observation personnalisée</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="etape11-navigation">
        <button onClick={onBack} className="etape11-btn-back">
          ← Retour
        </button>
        <button onClick={onComplete} className="etape11-btn-next">
          Continuer →
        </button>
      </div>

      {showObservationModal && (
        <ObservationModal
          isOpen={showObservationModal}
          onClose={() => {
            setShowObservationModal(false);
            setEditingObservation(null);
            setObsContextFromPending(null);
            setObsInitialPhotosFromPending([]);
            setObsPendingPhotoId(null);
          }}
          onSave={handleSaveObservation}
          observation={editingObservation}
          context={obsContextFromPending}
          initialPhotos={obsInitialPhotosFromPending}
          missionId={mission?.id}
        />
      )}
    </div>
  );
}
